import React from "react";

import DataTable from "../../../DataTable";
import ScenarioDescriptions from "./ScenarioDescriptions";

import useOutputReportStore from "../../../../store/OutputReport.store";
import {
  formatDataTableColumns,
  getOutputType,
} from "../../../../utils/helpers/dataTable";
import { dataTableCopy } from "../../../../utils/resources/dataTableCopy";
import {
  defaultScenarioCopy,
  expandedScenarioCopy,
} from "../../../../utils/resources/scenarioDescriptionsCopy";
import "./scenarioDescriptions.scss";

const getTableInfo = ({ reportType }) => {
  if (reportType === "cash") return;

  return (
    <div>
      The scenario is modeled as a parallel shift to the US treasury curve. This
      data represents <strong>total return (%) [4]</strong>
    </div>
  );
};

const getFootnote = ({ reportType, assetType }) => {
  const defaultFootnote = (
    <>
      {dataTableCopy.proposalNotMaintained}
      <br />
      ***{dataTableCopy.hypotheticalInterestRate}
    </>
  );

  if (reportType === "cash" && assetType === "tbill") {
    return (
      <>
        <ScenarioDescriptions scenarioText={defaultScenarioCopy} />
        <br />
        {dataTableCopy.unlessOtherwiseStated}
        <br />
        [1]{dataTableCopy.targetPortfolios}
        <br />
      </>
    );
  }

  if (reportType === "cash" && assetType === "corp") {
    return (
      <>
        <ScenarioDescriptions scenarioText={expandedScenarioCopy} />
        <br />
        {dataTableCopy.unlessOtherwiseStated}
        <br />
        [1]{dataTableCopy.targetPortfolios}
      </>
    );
  }
  if (reportType === "cash" && assetType === "muni") {
    return (
      <>
        <ScenarioDescriptions scenarioText={expandedScenarioCopy} />
        <br />
        {dataTableCopy.unlessOtherwiseStated}
        <br />
        [1]{dataTableCopy.proposalNotMaintained}
      </>
    );
  }

  if (reportType === "cash" && assetType === "treasury") {
    return (
      <>
        <ScenarioDescriptions scenarioText={expandedScenarioCopy} />
        {dataTableCopy.unlessOtherwiseStated}
        <br />
        [1]{dataTableCopy.targetPortfolios}
      </>
    );
  }

  if (reportType === "cash" && assetType !== "treasury")
    return (
      <>
        <ScenarioDescriptions scenarioText={expandedScenarioCopy} />
        {defaultFootnote}
      </>
    );

  return (
    <>
      <ScenarioDescriptions scenarioText={expandedScenarioCopy} />
      <>
        {dataTableCopy.unlessOtherwiseStated}
        <br />
        [1]{dataTableCopy.existingPortfolioInformation}
        <br />
        [2]{dataTableCopy.modelAndTransitionAnalysis}
        <br />
        [3]{dataTableCopy.seeReferenceIndex}
        <br />
        [4]{dataTableCopy.hypotheticalInterestRate}
      </>
    </>
  );
};

const ScenarioAnalysisDataTable = () => {
  const reportName = "Scenario Analysis"; // Refer to corresponding API Key
  const data = useOutputReportStore((state) => state.data);
  const types = useOutputReportStore((state) => state.types);

  if (!data || !types) {
    return null;
  }

  const { reportType, assetType } = getOutputType(data);
  const tableRowKeys = [
    {
      label: "Disinflation",
      key: "Disinflation",
    },
    {
      label: "Reflation",
      key: "Reflation",
    },
    {
      label: "Inflation Overheating",
      key: "Inflation Overheating",
    },
    {
      label: "Stagflation",
      key: "Stagflation",
    },
  ];

  let tableProps = {};
  switch (reportType) {
    case "cash":
      tableProps = {
        tableRowKeys: tableRowKeys,
        variation: "basic",
      };
      break;
    case "transition":
    default:
      tableProps = {
        tableRowKeys: tableRowKeys,
        variation: "stylized",
      };
      break;
  }

  return (
    <DataTable
      reportType={reportType}
      variation="stylized"
      tableCaption={`${reportName} Data Table`}
      tableRowHeaderKey="Scenario Name"
      tableRowKeys={tableProps.tableRowKeys}
      tableColumns={formatDataTableColumns(reportName, data)}
      tableDataTypes={types?.[reportName]}
      formatCellDataBy="row"
      footnote={getFootnote({ reportType, assetType })}
      info={getTableInfo({ reportType, assetType })}
    />
  );
};

export default ScenarioAnalysisDataTable;
